<template>
    <div class="vue-receipt receipt-outer-wrapper col-lg-6 col-xl-5 offset-xl-1">
        <div class="background-wrapper">
            <div class="vue-reservation-receipt">

                <div id="receipt" class="receipt-wrapper" aria-labelledby="receipt-title">
                    <h2 id="receipt-title" class="virtual-h3 d-lg-block">{{ translations.overviewOfYourOrder }}</h2>

                    <div class="show-on-open">
                        <!--Title and subtitle-->
                        <template v-if="hasActiveVehicle">
                            <div class="row mb-3">
                                <div class="col-8">
                                    <h3 class="active-vehicle-title mb-0 color-text">
                                        {{ getActiveVehicle.title }}
                                        <template v-if="getActiveVehicle.external_id">({{
                                                getActiveVehicle.external_id
                                            }})
                                        </template>

                                        <button class="information-button" @click="handleVehicleMoreInformation">
                                            {{ translations.moreInformation }}
                                        </button>
                                    </h3>
                                    <p class="mb-0 text-italic">{{ getActiveVehicle.subtitle }}</p>
                                </div>

                                <div class="col-4">
                                    <img :src="getActiveVehicle.images[0]" alt="">
                                </div>
                            </div>
                        </template>

                        <hr class="gray-divider">

                        <div class="row rental-period-wrapper md-4">
                            <div class="col-10">
                                <div class="inner-wrapper">
                                    <div class="rental-col">
                                        <h4 class="mb-0 color-text-light">{{ translations.departureLabel }}</h4>
                                        <span class="font-weight-bold d-block rental-date">{{
                                                getDepartureDate | receiptDate
                                            }}</span>
                                        <span class="rental-time">{{ getDepartureTime }}</span>
                                    </div>
                                    <div class="divider"></div>
                                    <div class="rental-col">
                                        <h4 class="mb-0 color-text-light">{{ translations.returnLabel }}</h4>
                                        <span class="font-weight-bold d-block rental-date">{{
                                                getReturnDate | receiptDate
                                            }}</span>
                                        <span class="rental-time">{{ getReturnTime }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <hr class="gray-divider">

                        <template v-if="hasActiveLocation">
                            <div class="mb-3 location-wrapper">
                                <h4 class="mb-0 color-text-light">{{ translations.locationLabel }}</h4>
                                {{ getActiveLocation.name }}

                                <p v-if="hasActiveLocationOpeningHours" class="mb-0">
                                    {{ getActiveLocationOpeningHours.openinghours.translatedDate }} geopend
                                    van {{ getActiveLocationOpeningHours.openinghours.starthours }} tot
                                    {{ getActiveLocationOpeningHours.openinghours.endhours }}
                                </p>
                            </div>

                            <div v-if="hasActivePickupPoint && hasMultiplePickupPoints"
                                 class="mb-3 pickup-point-wrapper">
                                <h4 class="mb-0 color-text-light">{{ translations.pickupPointSelectorLabel }}</h4>
                                {{ getActivePickupPoint.name }}<br/>
                                {{ getActivePickupPoint.streetName }} {{ getActivePickupPoint.houseNumber }}<br/>
                                {{ getActivePickupPoint.postalCode }} {{ getActivePickupPoint.place }}
                            </div>
                        </template>

                        <template v-if="getActiveVehicle.body_work?.id || getActiveVehicle.fuel?.id || getActiveVehicle.transmission?.id">
                            <hr v-if="hasActiveLocation" class="gray-divider">

                            <ul class="vehicle-specs-list">
                                <li v-if="getActiveVehicle.body_work?.id">
                                    <component :is="getIcon(getActiveVehicle.body_work.id)" />
                                    {{ getActiveVehicle.body_work.label }}
                                </li>
                                <li v-if="getActiveVehicle.fuel?.id">
                                    <component :is="getIcon(getActiveVehicle.fuel.id)" />
                                    {{ getActiveVehicle.fuel.label }}
                                </li>
                                <li v-if="getActiveVehicle.transmission?.id">
                                    <component :is="getIcon(getActiveVehicle.transmission.id)" />
                                    {{ getActiveVehicle.transmission.label }}
                                </li>
                            </ul>
                        </template>

                        <!--Loading-->
                        <template v-if="getLoadingVehicleRates || getExternalPricesLoading">
                            <div class="d-flex align-items-center justify-content-center my-3">
                                <div class="loading" style="width: 30px; height: 30px;"></div>
                                {{ translations.loadingText }}
                            </div>
                        </template>

                        <div class="price-table" v-if="hasActiveLocation">
                            <!--Rates-->
                            <div class="vehicle-rates-wrapper">
                                <table class="w-100">
                                    <tbody>
                                    <aut-vehicle-rate-line
                                        v-for="(vehicleRate, index) in getVehicleRates"
                                        :index="index"
                                        :key="index"
                                        :item="vehicleRate"
                                        :number_of_days="getDays"
                                    ></aut-vehicle-rate-line>
                                    </tbody>
                                </table>

                                <div class="free-kilometers-wrapper"
                                     v-if="getActiveVehicle.free_kilometers > 0">
                                    <p class="mb-0">
                                        {{ getFreeKilometers }} {{ translations.freeKilometers }} (&euro;
                                        {{ computedExtraKilometersPrice | vatExcluder | currencyFormatter }} per extra
                                        km)
                                    </p>
                                    <button @click="handleModal" class="information-button">
                                        {{ translations.moreInformation }}
                                    </button>
                                </div>
                            </div>


                            <!--Options-->
                            <div class="option-rates-wrapper mt-5"
                                 v-if="(hasActiveRentalOptions) && !getExternalPricesLoading">
                                <table class="w-100">
                                    <thead class="font-weight-bold">
                                    <tr>
                                        <th class="text-start"><h3 class="extra-options-title mb-1">
                                            {{ translations.extraOptionsTitle }}</h3></th>
                                        <th class="color-transparent text-end">{{ translations.totalText }}</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    <aut-option-rate-line
                                        v-for="option in getActiveRentalOptions"
                                        :key="option.tid"
                                        :option="option"
                                        :external-price-object="getExternalRentalOptionPrices[option.id]"
                                    ></aut-option-rate-line>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="subtotals" v-if="(hasActiveRentalOptions)">
                        <table class="w-100">
                            <tbody>
                            <tr>
                                <td class="font-weight-bold">Huurprijs</td>
                                <td class="text-end font-weight-bold">
                                    {{ getSubTotalRentalPrice | vatExcluder | currencyFormatter }}
                                </td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold">Extra opties</td>
                                <td class="text-end font-weight-bold">
                                    {{ getOptionsTotalRentalPrice | vatExcluder | currencyFormatter }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="discount-wrapper" v-if="hasDiscount || getVehicleRates[1]">
                        <h4 id="discountBox" :class="{opened: getShowDiscount}" @click="handleDiscountClick">
                            {{ translations.openDiscountText }}
                            <span>
                            {{ getEffectiveDiscount + getLongRentDiscount | vatExcluder | currencyFormatter }}
                          </span>
                        </h4>


                        <table :class="{'discount-inset': getShowDiscount, 'discount-folded': !getShowDiscount}">
                            <tbody>
                            <tr v-if="getShowDiscount && getVehicleRates[1]">
                                <td>
                                    {{ getVehicleRates[1].rateType }}
                                </td>
                                <td class="text-end font-weight-bold">
                                    {{ getVehicleRates[1].price | vatExcluder | currencyFormatter }}
                                </td>
                            </tr>
                            <tr v-if="getShowDiscount && (getActiveDiscountCode && hasDiscount)">
                                <td>
                                    Kortingscode: {{ getActiveDiscountCode }} - <button @click="removeDiscountCode">(Verwijder)</button>
                                </td>
                                <td class="text-end font-weight-bold">
                                    -{{ getEffectiveDiscount | vatExcluder | currencyFormatter }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="rental-price-wrapper" v-if="hasActiveLocation">
                        <p class="mt-2 mb-0">
                            <template v-if="isBusiness"> {{ translations.rentalPriceBusiness }}</template>
                            <template v-if="!isBusiness"> {{ translations.rentalPriceConsumer }}</template>
                            <span>{{ getTotalRentalPrice | vatExcluder | currencyFormatter }}</span>
                        </p>
                    </div>

                    <!--Add to calendar-->
                    <div class="mt-5" v-if="isThankYouPage">
                        <h3 class="virtual-p font-weight-bold color-text">{{ translations.addToCalendar }}</h3>

                        <div class="d-flex justify-content-between">
                            <a :href="icalCalendarUrl" class="d-block text-align-center" target="_blank">
                                <small class="color-text ">iCal</small>
                            </a>

                            <a :href="icalCalendarUrl" class="d-block text-align-center" target="_blank">
                                <small class="color-text ">Outlook Calender</small>
                            </a>

                            <a :href="googleCalendarUrl" class="d-block text-align-center" target="_blank">
                                <small class="color-text ">Google Calender</small>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="free-kilometers-description">
            <!-- consument huren -->
            <div v-if="hasFreeKilometers && !isBusiness">
                {{ translations.rentalPrice }} is op basis van {{ getFreeKilometers }} vrije kilometers,
                exclusief borg en brandstof (incl. elektriciteit).  Daarnaast rekenen we bij EV’s een kWh prijs per gereden kilometer (oplaadkosten).
            </div>
            <div v-if="!hasFreeKilometers && !isBusiness">
                {{ translations.rentalPrice }} is exclusief borg en brandstof.
            </div>

            <!-- zakelijk huren -->
            <div v-if="hasFreeKilometers && isBusiness">
                {{ translations.rentalPrice }} is op basis van {{ getFreeKilometers }} vrije kilometers,
                exclusief borg en brandstof (incl. elektriciteit).  Daarnaast rekenen we bij EV’s een kWh prijs per gereden kilometer (oplaadkosten).
            </div>

            <div v-if="!hasFreeKilometers && isBusiness">
                {{ translations.rentalPrice }} is exclusief borg en brandstof.
            </div>
        </div>

        <aut-contact-person-card
            v-if="contactPersonBusiness && hasContactPerson && this.contactPersonBusiness && isBusiness && !isThankYouPage"
            :contact-person="contactPersonBusiness"
        ></aut-contact-person-card>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import CurrencyFormatter from "../../mixins/CurrencyFormatter";
import translations from "../../mixins/translations";
import DateFormatter from "../../mixins/DateFormatter";
import VehicleRateLine from "./VehicleRateLine";
import OptionRateLine from "./OptionRateLine";
import ModalWrapper from "./ModalWrapper";
import VehicleModalWrapper from "../Vehicle/ModalWrapper.vue";
import DiscountPopup from "../Reservation/DiscountPopup.vue";
import moment from "moment";
import router from "../../router";
import ContactPersonCard from "./ContactPersonCard.vue";
import icon from "../../mixins/icon";

export default {
    name: "Receipt",

    mixins: [translations, DateFormatter, CurrencyFormatter, icon],

    props: {
        isThankYouPage: {
            type: Boolean,
            default: false,
        },
        extraKilometersPrice: {
            type: String,
            default: '',
        },
        contactPersonBusiness: {
            type: Array | Object,
            default: () => {
            }
        }
    },

    components: {
        'aut-vehicle-rate-line': VehicleRateLine,
        'aut-option-rate-line': OptionRateLine,
        'aut-contact-person-card': ContactPersonCard,
    },

    data() {
        return {
            open: true,
            openDiscount: false,
            isBusiness: false,
            discountCode: '',
        }
    },

    computed: {
        ...mapGetters([
            'getVehicleRates',
            'getLoadingVehicleRates',
            'getActiveVehicle',
            'getActiveLocation',
            'getActiveLocationOpeningHours',
            'getActivePickupPoint',
            'getDepartureDate',
            'getDepartureTime',
            'getReturnDate',
            'getReturnTime',
            'getActiveRentalOptions',
            'getActiveDiscount',
            'getActiveDiscountCode',
            'getShowDiscount',
            'getDays',
            'getKilometers',
            'getCurrentStep',
            'getStartDate',
            'getEndDate',
            'getTotalRentalPrice',
            'getLongRentDiscount',
            'getEffectiveDiscount',
            'getSubTotalRentalPrice',
            'getOptionsTotalRentalPrice',
            'getExternalRentalOptionPrices',
            'getExternalPricesLoading',
            'hasDiscount',
            'hasActiveLocationOpeningHours'
        ]),

        activeVehicleIsElectric() {
            if (lodash.isEmpty(this.getActiveVehicle.fuel)) {
                return true;
            }

            return lodash.isEqual(this.getActiveVehicle.fuel.id, 'electric');
        },

        hasActiveVehicle() {
            return !lodash.isEmpty(this.getActiveVehicle);
        },

        hasActiveLocation() {
            return !lodash.isEmpty(this.getActiveLocation);
        },

        hasActivePickupPoint() {
            return !lodash.isEmpty(this.getActivePickupPoint);
        },

        hasMultiplePickupPoints() {
            return this.getActiveLocation.pickupPoints.length > 1;
        },

        hasActiveRentalOptions() {
            return !lodash.isEmpty(this.getActiveRentalOptions);
        },

        icalCalendarUrl() {
            return encodeURI(
                'data:text/calendar;charset=utf8,' + [
                    'BEGIN:VCALENDAR',
                    'VERSION:2.0',
                    'BEGIN:VEVENT',
                    'URL:' + document.URL,
                    'DTSTART:' + (moment(this.getStartDate).toISOString(true).replace(/-|:|\.\d+/g, '') || ''),
                    'DTEND:' + (moment(this.getEndDate).toISOString(true).replace(/-|:|\.\d+/g, '') || ''),
                    'SUMMARY: Auto huren bij' + (this.getActiveLocation.name || ''),
                    'DESCRIPTION: Gehuurde auto: ' + (this.getActiveVehicle.title || '') + ' ' + (this.getActiveVehicle.subtitle || ''),
                    'LOCATION:' + (this.getActiveLocation.name || ''),
                    'END:VEVENT',
                    'END:VCALENDAR'].join('\n'));
        },

        googleCalendarUrl() {
            let startDate = moment(this.getStartDate).toISOString(true).replace(/-|:|\.\d+/g, '');
            let endDate = moment(this.getEndDate).toISOString(true).replace(/-|:|\.\d+/g, '');

            return encodeURI([
                `https://www.google.com/calendar/render?action=TEMPLATE&text=Auto huren bij ${this.getActiveLocation.name}`,
                `&dates=${startDate}/${endDate}`,
                `&details=Gehuurde auto: ${this.getActiveVehicle.title}, ${this.getActiveVehicle.subtitle}&location=${this.getActiveLocation.name}&sprop=&sprop=name:`,
            ])
        },

        hasFreeKilometers() {
            if (lodash.isNil(this.getActiveVehicle.free_kilometers)) {
                return false
            }

            return this.getActiveVehicle.free_kilometers > 0;
        },

        getFreeKilometers() {
            return (this.getKilometers * this.getDays);
        },

        computedExtraKilometersPrice() {
            if (lodash.isNil(this.getActiveVehicle.surcharge_per_km)) {
                return this.extraKilometersPrice;
            }

            return parseFloat(this.getActiveVehicle.surcharge_per_km).toFixed(2);
        },

        hasContactPerson() {
            return !lodash.isEmpty(this.contactPersonBusiness?.body)
        },
    },

    created() {
        this.isBusiness = router.currentRoute.meta?.isBusiness ?? false;

        if ('zakelijk' in router.currentRoute.query) {
            this.isBusiness = true;
        }

        if (this.isThankYouPage) {
            this.setStoreStateFromSessionStorage();
        }
    },

    methods: {
        handleClick() {
            this.open = !this.open;
        },

        handleDiscountClick() {
            this.openDiscount = !this.openDiscount;
            this.setShowDiscount(this.openDiscount);
        },

        handleModal() {
            this.$modal.show(ModalWrapper, {
                props: {
                    description: `<p>${this.getActiveVehicle.free_kilometers_info_text ??
                    'Dit is het totaal aantal vrije kilometers voor de hele verhuurperiode. ' +
                    `Eventuele extra km’s worden achteraf verrekend, hiervoor betaal je &euro; ${this.computedExtraKilometersPrice} per extra gereden kilometer. ` +
                    'Dit is exclusief brandstof, die betaal je altijd zelf. ' +
                    `Dit is inbegrepen in de &euro; ${this.computedExtraKilometersPrice} cent per kilometer.`
                    }</p>`,
                },
                label: `${this.translations.moreInformation} ${lodash.lowerCase(this.translations.freeKilometers)}`,
                transition: 'fade',
            });
        },

        handleDiscountPopup() {
            this.$modal.show(DiscountPopup, {
                props: {
                    title: `${window.Drupal.t('Discount code')}`,
                },
                transition: 'fade',
                classes: 'vue-pop-up-discount',
            });
        },

        handleVehicleMoreInformation() {
            this.$modal.show(VehicleModalWrapper, {
                props: {
                    id: this.getActiveVehicle.id,
                    item: this.getActiveVehicle,
                    hideCtas: true,
                },
                label: `${window.Drupal.t('Vehicle')} modal ${this.getActiveVehicle.title}`,
                transition: 'fade',
            });
        },

        ...mapActions([
            'setStoreStateFromSessionStorage',
            'setShowDiscount',
            'setDiscountCode',
            'removeDiscountCode'
        ]),
    },

    watch: {
        $route(to, from) {
            this.open = false
        }
    }
}
</script>
